import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode
} from 'react';
import { useDebouncedCallback } from '@react-hookz/web';
import { usePlayers } from '@hooks/usePlayers';
import type { Player, PlayerPosition } from '@api-service/players';

type PlayerPickerContextValue = {
  /** Candidates for the open slot only; empty while another slot's results are still on screen. */
  players: Player[];
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => void;
  activeSlotId: number | null;
  activePosition: PlayerPosition | null;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  openSlot: (slotId: number, position: PlayerPosition) => void;
  closeSlot: () => void;
};

const PlayerPickerContext = createContext<PlayerPickerContextValue | null>(
  null
);

export const usePlayerPicker = (): PlayerPickerContextValue => {
  const ctx = useContext(PlayerPickerContext);
  if (!ctx) {
    throw new Error(
      'usePlayerPicker must be used within a PlayerPickerProvider'
    );
  }
  return ctx;
};

// Long enough that a fast typist issues one request per word, short enough
// that the list still feels live.
export const SEARCH_DEBOUNCE_MS = 300;

// Single-letter terms match too much of the player set to be useful.
const MINIMUM_SEARCH_LENGTH = 2;

export const PlayerPickerProvider = ({ children }: { children: ReactNode }) => {
  const [activeSlotId, setActiveSlotId] = useState<number | null>(null);
  const [activePosition, setActivePosition] = useState<PlayerPosition | null>(
    null
  );
  const [searchTerm, setSearchTermRaw] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const applyDebouncedSearch = useDebouncedCallback(
    (value: string) => setDebouncedSearch(value),
    [],
    SEARCH_DEBOUNCE_MS
  );

  const isSlotOpen = activeSlotId !== null;
  const searchParam =
    debouncedSearch.length >= MINIMUM_SEARCH_LENGTH
      ? debouncedSearch
      : undefined;

  const {
    data,
    isLoading,
    isFetching,
    isPreviousData,
    hasNextPage,
    fetchNextPage
  } = usePlayers({
    position: activePosition ?? undefined,
    search: searchParam,
    sortBy: 'ownership',
    sortDirection: 'desc',
    limit: 100,
    // The picker only needs identity/price/ownership to render candidates;
    // captain selection now happens server-side at rating time, so prediction
    // enrichment can be skipped for a faster response.
    exclude: 'predictions',
    enabled: isSlotOpen
  });

  // React Query is configured to keep the previously resolved page while a new
  // request is in flight, so the raw results can belong to a slot the user has
  // already moved on from. Thirteen display names are shared by players in
  // different positions (Palmer is both a Chelsea midfielder and an Ipswich
  // goalkeeper), so serving another slot's results makes it possible to pick a
  // player of the wrong position without noticing. Only ever offer candidates
  // that match the slot being filled.
  const players = useMemo(() => {
    if (!activePosition) return [];
    return (data?.pages.flatMap((page) => page.data) ?? []).filter(
      (player) => player.position === activePosition
    );
  }, [data, activePosition]);

  // Nothing valid to show yet: either the first request is running, or React
  // Query is still holding results from an earlier query key. Retained results
  // outlive both the slot and the search term that produced them, so they are
  // never a safe list to offer, however many of them survive the position
  // filter above.
  const isAwaitingSlotResults = isLoading || isPreviousData;

  const clearSearch = useCallback(() => {
    setSearchTermRaw('');
    // Clear the debounced term immediately and re-target the pending trailing
    // call, so a term typed into the slot just closed can never be queried
    // against the next one while its search box reads empty.
    setDebouncedSearch('');
    applyDebouncedSearch('');
  }, [applyDebouncedSearch]);

  const openSlot = useCallback(
    (slotId: number, position: PlayerPosition) => {
      setActiveSlotId(slotId);
      setActivePosition(position);
      clearSearch();
    },
    [clearSearch]
  );

  const closeSlot = useCallback(() => {
    setActiveSlotId(null);
    setActivePosition(null);
    clearSearch();
  }, [clearSearch]);

  const setSearchTerm = useCallback(
    (value: string) => {
      setSearchTermRaw(value);
      applyDebouncedSearch(value);
    },
    [applyDebouncedSearch]
  );

  const value = useMemo<PlayerPickerContextValue>(
    () => ({
      players,
      isLoading: isAwaitingSlotResults,
      isFetching,
      hasNextPage,
      fetchNextPage,
      activeSlotId,
      activePosition,
      searchTerm,
      setSearchTerm,
      openSlot,
      closeSlot
    }),
    [
      players,
      isAwaitingSlotResults,
      isFetching,
      hasNextPage,
      fetchNextPage,
      activeSlotId,
      activePosition,
      searchTerm,
      setSearchTerm,
      openSlot,
      closeSlot
    ]
  );

  return (
    <PlayerPickerContext.Provider value={value}>
      {children}
    </PlayerPickerContext.Provider>
  );
};
